'use client';

import React from 'react';
import styled from '@benzinga/themetron';
import classNames from 'classnames';
import { isOdd } from '@benzinga/utils';

interface AttributeItemProps {
  label: string;
  value: string | number;
}

export interface AttributesTableProps {
  data: {
    label: string;
    value: string;
  }[];
}

export const AttributesTable: React.FC<AttributesTableProps> = ({ data }) => {
  if (!data || !Array.isArray(data)) return null;

  const filteredItems = data.filter(item => item.value !== '–');

  if (isOdd(filteredItems.length)) {
    data.push({ label: 'placeholder', value: '' });
  }

  return (
    <Container className="attributes-table">
      {filteredItems.map(item => (
        <AttributeItem key={item.label} label={item.label} value={item.value} />
      ))}
    </Container>
  );
};

const Container = styled.div`
  &.attributes-table {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;

    .attributes-item {
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    @media (min-width: 600px) {
      flex-direction: row;

      .attributes-item {
        min-height: 25px;

        &:nth-of-type(odd) {
          padding-right: 30px;
        }
      }
    }
  }
`;

export const AttributeItem = ({ label, value }: AttributeItemProps) => {
  return (
    <StyledAttributeItem className={classNames('attributes-item', { placeholder: label === 'placeholder' })}>
      {label === 'placeholder' ? null : (
        <>
          <span className="label">{label}</span>
          <span className="value">{value || '–'}</span>
        </>
      )}
    </StyledAttributeItem>
  );
};

const StyledAttributeItem = styled.div`
  &.attributes-item {
    display: flex;
    justify-content: space-between;
    width: 100%;
    color: ${({ theme }) => theme.colorPalette.gray700};
    font-size: ${({ theme }) => theme.fontSize.base};
    line-height: 30px;

    .label {
      margin-right: 0.5rem;
    }

    .value {
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
    }
  }
`;
